import { getNowMs } from "./serverTime";

// Format currency
export const formatCurrency = (amount, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

// Format date
export const formatDate = (date, options = {}) => {
  const defaultOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options,
  };

  return new Intl.DateTimeFormat("en-US", defaultOptions).format(
    new Date(date)
  );
};

// UTC 시간을 한국 시간으로 변환
export const convertUTCToKoreaTime = (utcDateString) => {
  if (!utcDateString) return null;

  const utcDate = new Date(utcDateString);
  const koreaTime = new Date(utcDate.getTime() + 9 * 60 * 60 * 1000); // UTC+9
  return koreaTime;
};

// 한국 시간을 UTC로 변환
export const convertKoreaTimeToUTC = (koreaDate) => {
  if (!koreaDate) return null;

  const date = new Date(koreaDate);

  // 한국 시간대 정보가 있는 경우, 정확한 UTC 변환
  const isKoreaTime =
    date.toString().includes("GMT+0900") ||
    date.toString().includes("한국 표준시");

  if (isKoreaTime) {
    // 한국 시간에서 9시간을 빼서 UTC로 변환
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // 한국 시간에서 9시간을 빼서 UTC로 변환
    const utcHours = hours - 9;

    // 시간이 음수가 되면 전날로 이동
    if (utcHours < 0) {
      const prevDay = new Date(year, month, day - 1);
      const utcDate = new Date(
        prevDay.getFullYear(),
        prevDay.getMonth(),
        prevDay.getDate(),
        utcHours + 24,
        minutes,
        seconds
      );

      return utcDate;
    } else {
      // UTC 시간을 직접 생성 (로컬 시간대 변환 방지)
      const utcDate = new Date(
        Date.UTC(year, month, day, utcHours, minutes, seconds)
      );

      return utcDate;
    }
  } else {
    // 일반적인 변환
    const utcTime = new Date(date.getTime() - 9 * 60 * 60 * 1000);

    // console.log("🔄 한국시간 → UTC 변환:", {
    //   koreaTime: date.toString(),
    //   utcTime: utcTime.toISOString(),
    // });

    return utcTime;
  }
};

// 한국 시간 기준으로 날짜 문자열 반환 (YYYY-MM-DD 형식)
export const getKoreaDateString = (date = new Date()) => {
  const koreaTime = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return koreaTime.toISOString().split("T")[0];
};

// 한국 시간 기준으로 내일 날짜 반환
export const getKoreaTomorrowDate = () => {
  const now = new Date();
  const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const tomorrow = new Date(koreaTime);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle function
export const throttle = (func, limit) => {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Generate random ID
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

// Validate email
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Capitalize first letter
export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Truncate text
export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + "...";
};

// 참여 현황 계산 함수 (퍼센트, 소수점 절사)
// - totalUseQuantity 는 서버가 광고 기간 경과 비율로 계산해서 내려줌 → 그대로 사용
// - 응답에 appConfigs 가 있으면 가공 전 서버 응답(실제 수치)이므로 기존 기간 경과 계산 사용
//   (서버 배포 전 과도기 호환용)
export const calculateParticipationStatus = (ad) => {
  if (!ad || !ad.totalQuantity) {
    return "0%";
  }

  if (!("appConfigs" in ad)) {
    const used = Number(ad.totalUseQuantity) || 0;
    const percent = Math.floor((used / ad.totalQuantity) * 100);
    return `${Math.min(100, Math.max(0, percent))}%`;
  }

  return calculateElapsedParticipation(ad);
};

// 기간 경과 비율 기반 참여 현황 (가공 전 서버 응답용)
const calculateElapsedParticipation = (ad) => {
  if (ad.status === "PENDING") {
    return "0%";
  }

  if (ad.status === "DONE") {
    return "100%";
  }

  if (ad.status === "APPROVED") {
    if (!ad.startDate || !ad.endDate) {
      return "0%";
    }

    const startDate = new Date(ad.startDate).getTime();
    const endDate = new Date(ad.endDate).getTime();
    const totalDuration = endDate - startDate;
    if (!totalDuration || totalDuration <= 0) {
      return "0%";
    }

    const elapsedDuration = Math.max(0, Date.now() - startDate);
    if (elapsedDuration >= totalDuration) {
      return "100%";
    }

    return `${Math.floor((elapsedDuration / totalDuration) * 100)}%`;
  }

  return "0%";
};

/** 외부 URL을 referrer 없이 새 탭으로 연다 (유입 경로 노출 방지) */
export const openExternalUrl = (url) => {
  if (!url) return;
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.referrerPolicy = "no-referrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

/**
 * 광고 마감일(endDate) + end_submit(KST) 마감 시각의 epoch ms
 * 예: endDate=2026-09-23, end_submit=17:00 → 2026-09-23 17:00 KST
 */
export const getAdEndSubmitDeadlineMs = (endDate, endSubmitTime = "17:00") => {
  if (!endDate) return null;
  const dateStr = String(endDate).split("T")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;

  const parts = String(endSubmitTime).split(":");
  const endHour = parseInt(parts[0], 10);
  const endMinute = parseInt(parts[1], 10);
  if (!Number.isFinite(endHour) || !Number.isFinite(endMinute)) return null;

  const hh = String(endHour).padStart(2, "0");
  const mm = String(endMinute).padStart(2, "0");
  const deadline = new Date(`${dateStr}T${hh}:${mm}:00+09:00`);
  const ms = deadline.getTime();
  return Number.isFinite(ms) ? ms : null;
};

const END_SUBMIT_TIME_REGEX = /^\d{1,2}:\d{2}$/;

/**
 * endDate(YYYY-MM-DD)가 속한 요일(KST 기준)에 맞는 end_submit 시각을 zoomSettings에서 고른다.
 * 토/일요일 전용 값이 없으면 평일(end_submit) 값으로 대체.
 * 광고의 마감일 자체가 토/일요일이면 그날의 마감시각을 봐야 하므로, "오늘" 요일이 아니라
 * endDate의 요일로 판단한다 (AddAdvertisement의 getMinStartDate와는 기준 날짜가 다름).
 */
export const resolveEndSubmitTimeForDate = (endDate, zoomSettings) => {
  const weekday =
    zoomSettings?.end_submit != null &&
    END_SUBMIT_TIME_REGEX.test(String(zoomSettings.end_submit))
      ? zoomSettings.end_submit
      : "17:00";

  const dateStr = String(endDate || "").split("T")[0];
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return weekday;

  // 문자열의 연/월/일을 그대로 UTC 자정으로 앵커링해서 요일 추출 (TZ 변환로 인한 날짜 밀림 방지)
  const day = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  ).getUTCDay(); // 0: 일, 6: 토

  if (day === 6) {
    return zoomSettings?.end_submit_sat != null &&
      END_SUBMIT_TIME_REGEX.test(String(zoomSettings.end_submit_sat))
      ? zoomSettings.end_submit_sat
      : weekday;
  }
  if (day === 0) {
    return zoomSettings?.end_submit_sun != null &&
      END_SUBMIT_TIME_REGEX.test(String(zoomSettings.end_submit_sun))
      ? zoomSettings.end_submit_sun
      : weekday;
  }
  return weekday;
};

/** 광고 마감일 end_submit 이후이면 연장 잠금 (마감일의 요일에 맞는 시각 기준) */
export const isAdExtensionLockedByEndSubmit = (
  endDate,
  zoomSettings,
  nowMs = getNowMs()
) => {
  const endSubmitTime = resolveEndSubmitTimeForDate(endDate, zoomSettings);
  const deadlineMs = getAdEndSubmitDeadlineMs(endDate, endSubmitTime);
  if (deadlineMs == null) return false;
  return nowMs >= deadlineMs;
};

/** 연장 잠금 시각까지 남은 ms (이미 지났으면 0 이하, 마감일의 요일에 맞는 시각 기준) */
export const getMsUntilAdExtensionLock = (
  endDate,
  zoomSettings,
  nowMs = getNowMs()
) => {
  const endSubmitTime = resolveEndSubmitTimeForDate(endDate, zoomSettings);
  const deadlineMs = getAdEndSubmitDeadlineMs(endDate, endSubmitTime);
  if (deadlineMs == null) return null;
  return deadlineMs - nowMs;
};

// ======================================================== 퀴즈 B(쇼핑 원부) ======================================================== //

// 퀴즈 B 저장 규칙 (등록·다시 받아오기 공통)
// - 광고 missionName / imageUrl: 원부 정보 API(crawlNaverSearchOriginProduct)의 productName / imageUrl
//   (name은 카드의 첫 span이라 "최저" 같은 가격 문구가 들어오므로 쓰지 않는다)
// - memo.originProductName / originPrice / adTag / imageUrl: 스마트스토어 parse-link 값
// - memo의 아래 키: 원부 정보 API 값 (imageUrl은 memo.imageUrl과 겹치므로 제외)
export const ORIGIN_INFO_MEMO_KEYS = [
  "name",
  "prependUnit",
  "shoppingProductPrice",
  "deliveryFee",
  "shoppingProductPriceTotal",
  "shoppingProductStar",
  "shoppingProductStarCount",
  "keep",
  "shoppingProductMallInformation",
];

// 원부 정보 API 응답에서 memo에 넣을 값만 추린다
export const pickOriginInfoMemo = (originData = {}) =>
  ORIGIN_INFO_MEMO_KEYS.reduce((acc, key) => {
    acc[key] = originData[key];
    return acc;
  }, {});

// 크롤링 결과의 HTML 엔티티(&amp; 등) 디코딩
export const decodeHtmlText = (text) => {
  if (!text) return text;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

// URL에서 상품 ID 추출 (AddAdvertisement의 extractProductIdFromUrl과 동일한 규칙)
export const extractProductIdFromUrl = (url) => {
  if (!url) return null;

  const urlString = url.toString();

  // @로 시작하는 경우 제거
  let cleanUrl = urlString.startsWith("@") ? urlString.substring(1) : urlString;

  // ? 뒤의 쿼리 파라미터 제거
  cleanUrl = cleanUrl.split("?")[0];

  // 네이버 플레이스 URL 패턴 (PC)
  const placeMatch = cleanUrl.match(/entry\/place\/(\d+)/);
  if (placeMatch) {
    return placeMatch[1];
  }

  // 네이버 플레이스 URL 패턴 (모바일)
  const mobilePlaceMatch = cleanUrl.match(/\/restaurant\/(\d+)\/home/);
  if (mobilePlaceMatch) {
    return mobilePlaceMatch[1];
  }

  // 네이버 스마트스토어 URL 패턴
  const shoppingMatch = cleanUrl.match(/\/products\/(\d+)/);
  if (shoppingMatch) {
    return shoppingMatch[1];
  }

  // 뒤에서부터 가장 긴 숫자를 반환
  const numberMatches = cleanUrl.match(/(\d+)/g);
  if (numberMatches && numberMatches.length > 0) {
    return numberMatches
      .reverse()
      .reduce((longest, current) =>
        current.length > longest.length ? current : longest,
      );
  }

  return null;
};
